// AudienceCarousel.jsx
import { useState, useEffect, useCallback, useRef } from "react";

export default function AudienceCarousel({ audiences = [], interval = 5000 }) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [noTransition, setNoTransition] = useState(() => new Set());
  const [dragDelta, setDragDelta] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const total = audiences.length;
  const pointerStartX = useRef(null);
  const pointerStartY = useRef(null);
  const activePointerId = useRef(null);
  const didDrag = useRef(false);
  const videoRefs = useRef([]);

  const computeOffset = useCallback(
    (index, ref) => {
      let d = index - ref;

      if (d > total / 2) d -= total;
      if (d < -total / 2) d += total;

      return d;
    },
    [total]
  );

  const goTo = useCallback(
    (updater) => {
      setCurrent((prev) => {
        const nextIdx =
          typeof updater === "function" ? updater(prev) : updater;

        if (total > 1) {
          const wraps = new Set();

          audiences.forEach((_, i) => {
            const oldOff = computeOffset(i, prev);
            const newOff = computeOffset(i, nextIdx);

            if (Math.abs(newOff - oldOff) > 1.5) {
              wraps.add(i);
            }
          });

          if (wraps.size) {
            setNoTransition(wraps);
            requestAnimationFrame(() =>
              requestAnimationFrame(() => setNoTransition(new Set()))
            );
          }
        }

        return ((nextIdx % total) + total) % total;
      });
    },
    [audiences, computeOffset, total]
  );

  const next = useCallback(() => goTo((c) => c + 1), [goTo]);
  const prev = useCallback(() => goTo((c) => c - 1), [goTo]);

  useEffect(() => {
    if (paused || isDragging || total <= 1) return;

    const id = setInterval(next, interval);
    return () => clearInterval(id);
  }, [paused, isDragging, next, total, interval]);

  // Only the active slide's video plays. Phones can't reliably decode more
  // than one or two videos at once, so playing all of them at once is the
  // main source of the carousel feeling laggy and draining battery. Pausing
  // the off-screen ones keeps playback smooth everywhere.
  useEffect(() => {
    videoRefs.current.forEach((vid, i) => {
      if (!vid) return;

      if (i === current) {
        const playPromise = vid.play();
        if (playPromise && typeof playPromise.catch === "function") {
          // Ignore autoplay-policy rejections; the poster frame still shows.
          playPromise.catch(() => {});
        }
      } else if (!vid.paused) {
        vid.pause();
      }
    });
  }, [current]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  const resetDrag = () => {
    pointerStartX.current = null;
    pointerStartY.current = null;
    activePointerId.current = null;
    didDrag.current = false;
    setDragDelta(0);
    setIsDragging(false);
  };

  const onPointerDown = (e) => {
    if (total <= 1) return;
    if (e.target.closest("button")) return;

    pointerStartX.current = e.clientX;
    pointerStartY.current = e.clientY;
    activePointerId.current = e.pointerId;
    didDrag.current = false;

    setPaused(true);
    setIsDragging(true);
    setDragDelta(0);

    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!isDragging) return;
    if (activePointerId.current !== e.pointerId) return;
    if (pointerStartX.current == null || pointerStartY.current == null) return;

    const dx = e.clientX - pointerStartX.current;
    const dy = e.clientY - pointerStartY.current;

    if (Math.abs(dx) > 6) {
      didDrag.current = true;
    }

    if (Math.abs(dx) > Math.abs(dy)) {
      e.preventDefault();
    }

    const dampedDx = dx * 0.82;
    setDragDelta(dampedDx);
  };

  const onPointerUp = (e) => {
    if (!isDragging) return;
    if (activePointerId.current !== e.pointerId) return;

    const dx =
      pointerStartX.current == null ? 0 : e.clientX - pointerStartX.current;

    if (Math.abs(dx) > 80) {
      if (dx > 0) {
        prev();
      } else {
        next();
      }
    }

    resetDrag();
    setPaused(false);
  };

  const onPointerCancel = () => {
    resetDrag();
    setPaused(false);
  };

  if (!total) return null;

  return (
    <div
      className="relative w-full select-none overflow-hidden bg-transparent py-10 text-left sm:py-16"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => {
        if (!isDragging) setPaused(false);
      }}
      role="region"
      aria-roledescription="carousel"
      aria-label="Feature highlights"
    >
      {/* Stage */}
      <div
        className={`relative flex h-[260px] w-full touch-pan-y items-center justify-center overflow-hidden xs:h-[320px] sm:h-[460px] md:h-[560px] lg:h-[640px] xl:h-[700px] ${
          isDragging ? "cursor-grabbing" : "cursor-grab"
        }`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onPointerLeave={(e) => {
          if (isDragging) onPointerUp(e);
        }}
      >
        {audiences.map((a, i) => {
          const offset = computeOffset(i, current);
          const abs = Math.abs(offset);
          const isActive = abs === 0;

          let scale = 1;
          let opacity = 1;

          if (abs === 1) {
            scale = 0.86;
            opacity = 0.55;
          } else if (abs === 2) {
            scale = 0.7;
            opacity = 0.18;
          } else if (abs > 2) {
            scale = 0.6;
            opacity = 0;
          }

          const skipTransition = noTransition.has(i) || isDragging;

          // Tighter offset so neighbors overlap with the active slide
          // for that cinematic peek-from-the-edges feel.
          const offsetPercent = offset * 56;

          return (
            <article
              key={a.title || i}
              aria-hidden={!isActive}
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${total}`}
              className={`absolute left-1/2 top-1/2 w-[78vw] max-w-[1100px] will-change-transform sm:w-[68vw] md:w-[62vw] lg:w-[56vw] xl:w-[52vw] ${
                skipTransition
                  ? "transition-none"
                  : "transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
              }`}
              style={{
                transform: `translate(-50%, -50%) translateX(calc(${offsetPercent}% + ${dragDelta}px)) scale(${scale})`,
                opacity,
                zIndex: 10 - abs,
                pointerEvents: isActive ? "auto" : "none",
              }}
            >
              <div className="group relative aspect-video w-full overflow-hidden rounded-[20px] bg-black ring-1 ring-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.55)] sm:rounded-[28px]">
                <video
                  ref={(el) => (videoRefs.current[i] = el)}
                  src={a.video}
                  playsInline
                  muted
                  loop
                  draggable={false}
                  preload={isActive ? "auto" : "none"}
                  aria-label={`${a.title} preview`}
                  className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                />

                {/* Soft dim on inactive slides so the active one pops */}
                {!isActive && (
                  <div className="pointer-events-none absolute inset-0 bg-black/55" />
                )}

                {/* Caption gradient — only on the active slide */}
                {isActive && (
                  <>
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-black/5 to-transparent" />

                    <div className="pointer-events-none absolute bottom-0 left-0 right-0 p-5 text-left text-white sm:p-7 md:p-8">
                      <div className="max-w-xl text-left">
                        <h3 className="font-display text-left text-xl font-semibold tracking-tight text-white sm:text-2xl md:text-3xl">
                          {a.title}
                        </h3>

                        {a.description && (
                          <p className="mt-2 max-w-[48ch] text-left text-sm leading-relaxed text-white/75 sm:text-base">
                            {a.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </article>
          );
        })}

        {/* Previous */}
        <button
          type="button"
          onClick={prev}
          aria-label="Previous slide"
          className="absolute left-3 top-1/2 z-30 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/55 text-white backdrop-blur-md transition hover:bg-black/80 active:scale-95 xs:h-12 xs:w-12 sm:left-8 sm:h-14 sm:w-14 md:left-12 md:h-16 md:w-16"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        {/* Next */}
        <button
          type="button"
          onClick={next}
          aria-label="Next slide"
          className="absolute right-3 top-1/2 z-30 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/55 text-white backdrop-blur-md transition hover:bg-black/80 active:scale-95 xs:h-12 xs:w-12 sm:right-8 sm:h-14 sm:w-14 md:right-12 md:h-16 md:w-16"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
      </div>

      {/* Dots */}
      <div className="relative z-30 mt-8 flex items-center justify-center gap-2 sm:mt-10">
        {audiences.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            aria-current={i === current}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === current
                ? "w-10 bg-white"
                : "w-2.5 bg-white/30 hover:bg-white/60"
            }`}
          />
        ))}
      </div>
    </div>
  );
}